import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Users, Plus, CheckCircle2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const AddToTeamModal = ({ talentId, talentName, isOpen, onClose }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen) fetchTeams();
  }, [isOpen]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await api.get('/teams');
      setTeams(res.data.teams || []);
    } catch {} 
    finally { setLoading(false); }
  };

  const addToTeam = async (teamId, teamName) => {
    setAdding(teamId);
    try {
      await api.post(`/teams/${teamId}/members`, { talent_ids: [talentId] });
      toast.success(`Added to ${teamName}`);
      onClose();
    } catch {
      toast.error('Failed to add');
    } finally { setAdding(null); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/teams', { team_name: newName.trim(), talent_ids: [talentId] });
      toast.success(`Created "${newName}" and added ${talentName || 'talent'}`);
      onClose();
    } catch {
      toast.error('Failed to create team');
    } finally { setCreating(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="add-to-team-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Add to Team</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : (
            <>
              {teams.map(team => {
                const alreadyIn = team.members?.some(m => m.talent_id === talentId);
                return (
                  <button
                    key={team.id}
                    disabled={alreadyIn || adding === team.id}
                    onClick={() => addToTeam(team.id, team.team_name)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg mb-1.5 text-left transition-all ${
                      alreadyIn ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-gray-100'
                    }`}
                    data-testid={`add-to-team-${team.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{team.team_name}</p>
                      <p className="text-[10px] text-gray-400">{team.member_count} members</p>
                    </div>
                    {alreadyIn ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : adding === team.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                    ) : (
                      <Plus className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                );
              })}

              {teams.length === 0 && !showCreate && (
                <p className="text-sm text-gray-400 text-center py-4">No teams yet</p>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50">
          {showCreate ? (
            <div className="flex gap-2">
              <Input
                value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Team name"
                className="text-sm"
                onKeyDown={e => e.key === 'Enter' && createAndAdd()}
                data-testid="new-team-input"
              />
              <Button size="sm" onClick={createAndAdd} disabled={creating || !newName.trim()} data-testid="create-add-btn">
                {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="w-full text-violet-600" onClick={() => setShowCreate(true)} data-testid="new-team-btn">
              <Plus className="w-4 h-4 mr-1" />Create New Team
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddToTeamModal;
